import React from "react";

import { css } from "@emotion/core";
import { useEffect } from "react";

import { useLocalStorage } from "@zapier/checkout/esm/hooks";
import { Currency, useCurrencies } from "@zapier/currency";
import {
  Alert,
  useTheme,
  Colors as ThemeColors,
  Typography,
} from "@zapier/design-system";

import { Border, Colors } from "@zapier/checkout/esm/constants";
import { PlanV2 } from "@zapier/checkout/esm/types";
import { useApolloInjection } from "@zapier/checkout/esm/contexts/ApolloInjection";
import { invoicePreviewQuery_account_invoicePreview as Invoice } from "@zapier/checkout/esm/graphql-types";

import { AmountDue } from "./AmountDue";
import { AmountDueOnNextInterval } from "./AmountDueOnNextInterval";
import { ChangePlanSummarySkeleton } from "./ChangePlanSummarySkeleton";
import { InvoiceHeader } from "./InvoiceLine/InvoiceHeader";
import { InvoiceLine } from "./InvoiceLine";

type Props = {
  invoice: Invoice | null | undefined;
  error: string | null | undefined;
  isLoading: boolean;
  plan: PlanV2;
};

const breakdownCss = {
  borderBottom: `${Border.size.default} solid ${Border.color.default}`,
  padding: 10,
};

const rebrandBreakdownCss = {
  borderBottom: "none",
  padding: 0,
};

const sectionCss = {
  backgroundColor: Colors.white,
  border: `${Border.size.default} solid ${Border.color.default}`,
  borderRadius: Border.radius.default,
  lineHeight: "1.6em",
  overflow: "hidden",
};

const rebrandSectionCss = {
  backgroundColor: "transparent",
  borderColor: ThemeColors.neutral400,
  ...Typography.paragraph3,
};

/* Exported to be re-usable across all line items of the summary */
export const tableItem = css`
  color: ${ThemeColors.neutral800};
  display: grid;
  grid-template-columns: 70% 30%;
  border-bottom: 1px solid ${ThemeColors.neutral400};

  & > span {
    padding: 10px;

    &:nth-of-type(2) {
      text-align: right;
      border-left: 1px solid ${ThemeColors.neutral400};
    }
  }
  @media (max-width: 769px) {
    grid-template-columns: 50% 50%;
  }
`;

export const ChangePlanSummary = ({
  error,
  invoice,
  isLoading,
  plan,
}: Props) => {
  const { isRebrand } = useTheme();
  const { useQuery } = useApolloInjection();
  const { currencies } = useCurrencies({ useQuery });
  const [invoicedAtDate, setInvoicedAtDate] = useLocalStorage("invoicedAtDate");

  useEffect(() => {
    if (!isLoading && invoice) {
      setInvoicedAtDate(invoice?.invoicedAt);
    }
  }, [invoice, invoicedAtDate, isLoading, setInvoicedAtDate]);

  if (isLoading || !invoice) {
    return <ChangePlanSummarySkeleton />;
  }

  if (error || !plan) {
    return <Alert type="error">{error}</Alert>;
  }

  const tiers = plan.tiers;
  const freeTier = tiers.find(tier => tier.pricePerTask === 0);
  const planNumFreeMeteredTasks = freeTier ? freeTier.numTasks : 0;

  return (
    <section css={[sectionCss, isRebrand && rebrandSectionCss]}>
      {invoice.items.length > 0 && (
        <div css={[breakdownCss, isRebrand && rebrandBreakdownCss]}>
          {isRebrand && <InvoiceHeader />}
          {invoice.items.map((line: any) => (
            <InvoiceLine key={line.id} line={line} />
          ))}
        </div>
      )}
      <AmountDue
        amountDue={invoice.amountDue}
        currency={currencies?.find(
          (c: Currency) => c.name === invoice.currency
        )}
      />
      {plan.amount > 0 && (
        <AmountDueOnNextInterval
          extraTerm={invoice?.metadata?.terms}
          isPlanMetered={tiers.length > 0}
          meteredTiers={tiers}
          nextPaymentDate={invoice.periodEnd}
          planBillingAmount={plan.amount}
          planInterval={plan.interval}
          planNumFreeMeteredTasks={planNumFreeMeteredTasks}
        />
      )}
    </section>
  );
};
